import { AlertCircle, CheckCircle, FileText, Upload, X } from "lucide-react";
import { useRef, useState } from "react";
import { bugsnagClient } from "../bugsnag";
import { BACKEND_URL } from "../config/config";
import { ANALYTICS_EVENTS, trackEvent } from "../utils/analytics";

const PlanUploadWizard = ({
  onClose,
  projectId,
  sections,
  onUploadComplete,
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedSection, setSelectedSection] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    setError(null);

    if (file) {
      // Check for PDF or DWG files
      const isValidType =
        file.type === "application/pdf" ||
        file.name.toLowerCase().endsWith(".dwg");

      if (!isValidType) {
        const fileExt = file.name.split(".").pop()?.toLowerCase();
        setError(
          `Unsupported file type${
            fileExt ? ` (.${fileExt})` : ""
          }. Please select a PDF or DWG file.`,
        );
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        // 50MB limit
        setError("File size must be less than 50MB");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    setError(null);

    if (file) {
      // Check for PDF or DWG files
      const isValidType =
        file.type === "application/pdf" ||
        file.name.toLowerCase().endsWith(".dwg");

      if (!isValidType) {
        const fileExt = file.name.split(".").pop()?.toLowerCase();
        setError(
          `Unsupported file type${
            fileExt ? ` (.${fileExt})` : ""
          }. Please select a PDF or DWG file.`,
        );
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        setError("File size must be less than 50MB");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    if (!selectedSection) {
      setError("Please select a section for this plan");
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("plan", selectedFile);

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await fetch(
        `${BACKEND_URL}/api/projects/${projectId}/sections/${selectedSection}/plans`,
        {
          method: "POST",
          headers: {
            "x-auth-token": localStorage.getItem("token"),
          },
          body: formData,
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Upload failed");
      }

      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadComplete(true);

      trackEvent(ANALYTICS_EVENTS.PLAN_UPLOADED, {
        projectId,
        sectionId: selectedSection,
        fileType: selectedFile.name.split(".").pop()?.toLowerCase(),
      });

      // Call the completion handler
      if (onUploadComplete) {
        onUploadComplete();
      }

      // Auto close after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Upload failed:", error);
      setError(error.message || "Upload failed. Please try again.");
      setIsUploading(false);
      setUploadProgress(0);
      bugsnagClient.notify(error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Upload Floor Plan
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        {!uploadComplete ? (
          <>
            {/* Section Selection */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Section <span className="text-red-500">*</span>
                </label>
                {sections && sections.length > 0 ? (
                  <select
                    value={selectedSection}
                    onChange={(e) => setSelectedSection(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    required
                  >
                    <option value="">Choose a section...</option>
                    {sections.map((section) => (
                      <option key={section._id} value={section._id}>
                        {section.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded-md">
                    <p>
                      No sections available. Create a section first to organize
                      your plans.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {!selectedFile ? (
              <div className="space-y-4">
                {/* File Type Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg border">
                    <div className="text-2xl mb-2">📄</div>
                    <div className="text-sm font-medium text-gray-700">
                      PDF Plans
                    </div>
                    <div className="text-xs text-gray-500">
                      Architectural drawings, blueprints
                    </div>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg border">
                    <div className="text-2xl mb-2">📐</div>
                    <div className="text-sm font-medium text-gray-700">
                      DWG Files
                    </div>
                    <div className="text-xs text-gray-500">
                      AutoCAD drawings, technical plans
                    </div>
                  </div>
                </div>

                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors cursor-pointer"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-sm text-gray-600 mb-2">
                    Drop your PDF or DWG plan here, or{" "}
                    <span className="text-purple-600 font-medium">browse</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    PDF and DWG files up to 50MB
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.dwg,application/pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>

                {error && (
                  <div className="flex items-center space-x-2 text-red-600 text-sm">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="text-3xl">
                      {selectedFile.type === "application/pdf"
                        ? "📄"
                        : selectedFile.name.toLowerCase().endsWith(".dwg")
                          ? "📐"
                          : "📋"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {selectedFile.name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatFileSize(selectedFile.size)}
                        {selectedFile.type === "application/pdf"
                          ? " • PDF Document"
                          : selectedFile.name.toLowerCase().endsWith(".dwg")
                            ? " • DWG Drawing"
                            : " • Plan File"}
                      </p>
                    </div>
                  </div>
                </div>

                {isUploading && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Uploading...</span>
                      <span className="text-gray-600">{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="flex items-center space-x-2 text-red-600 text-sm">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    disabled={isUploading}
                  >
                    Choose Different File
                  </button>
                  <button
                    onClick={handleUpload}
                    disabled={
                      isUploading ||
                      !selectedSection ||
                      !sections ||
                      sections.length === 0
                    }
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isUploading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload size={16} className="mr-2" />
                        Upload Plan
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-6">
            <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Upload Complete!
            </h3>
            <p className="text-sm text-gray-600">
              Your floor plan has been uploaded successfully and is being
              processed.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanUploadWizard;
